import { useQuery } from '@apollo/client'
import type { PromptResolve } from '@ur-solutions/react-components'
import { Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  Checkbox,
  Label,
  RegularModal,
  SVGIcon,
  TextArea,
} from '@/components_deprecated'
import { format } from 'date-fns'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { USER_TIME_ENTRIES_QUERY } from '@/modules/timesheets/queries'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useModuleConfig, useNodes, useUser } from '@/utils/hooks'
import type { TerminalEntryNode } from '../types.graphql'

const CLOCK_ICON_WIDTH = '40px'

const Wrapper = styled.div`
  width: 300px;

  div.loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 120px;
  }
`
const Overlapping = styled.div`
  p {
    margin: 0;
  }

  div.entry {
    display: grid;
    grid-template-columns: ${CLOCK_ICON_WIDTH} 1fr;
    grid-template-rows: auto 1fr;
    gap: 0.25rem 0.5rem;
    margin-block: 1rem;
    align-items: start;

    div.icon {
      grid-row: span 2;
    }
    div.timespan {
      grid-column: 2;
      font-size: 0.8rem;
      font-weight: 600;

      span:first-of-type::after {
        content: ' - ';
        color: ${props => props.theme.colors.gray3};
      }
    }
    div.comment {
      grid-column: 2;
      font-size: 0.8rem;

      span {
        color: ${props => props.theme.colors.gray3};
      }
    }
    div.check {
      grid-column: 1 / -1;
      padding-top: 0.5rem;
    }
  }
`

interface CheckOutModalProps {
  timeEntry: TerminalEntryNode
  onResolve: PromptResolve<string | null>
}

export const CheckOutModal: React.JSX.CFC<CheckOutModalProps> = ({
  timeEntry,
  onResolve,
}) => {
  const translations = useTranslate({
    title: 'terminal.prompt.checkout-title',
    commentLabel: 'terminal.prompt.checkout-comment-label',
    overlapping: 'terminal.prompt.checkout-overlapping',
    noComment: 'common.no-comment',
    extend: 'terminal.prompt.checkout-extend-overlapping',

    cancel: 'common.cancel',
    save: 'common.save',
  })

  const user = useUser()
  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)
  const commentRequired = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_COMMENT_REQUIRED
  )

  const [comment, setComment] = useState('')
  const [extendOverlapping, setExtendOverlapping] = useState(false)

  const { data, loading } = useQuery(USER_TIME_ENTRIES_QUERY, {
    variables: {
      user: user.id,
      overlapsWith: [timeEntry.id],
    },
  })

  const overlappingEntry = useNodes(data?.allTimeEntries)[0]

  const { datetimeStart, datetimeEnd } = useMemo(() => {
    if (!overlappingEntry)
      return {
        datetimeStart: null,
        datetimeEnd: null,
      }

    const datetimeStart = format(
      new Date(overlappingEntry.datetimeStart),
      'dd.MM.yyyy HH:mm'
    )
    const datetimeEnd = !overlappingEntry.datetimeEnd
      ? null
      : format(new Date(overlappingEntry.datetimeEnd), 'dd.MM.yyyy HH:mm')

    return {
      datetimeStart,
      datetimeEnd,
    }
  }, [overlappingEntry])

  const submitDisabled = useMemo(() => {
    const missingComment = commentRequired && !comment.trim()

    if (loading) return true
    if (!overlappingEntry) return missingComment

    return missingComment || !extendOverlapping
  }, [comment, commentRequired, extendOverlapping, loading, overlappingEntry])

  const handleSubmit = useCallback(() => {
    if (submitDisabled) return

    onResolve(comment.trim())
  }, [comment, onResolve, submitDisabled])

  return (
    <RegularModal
      title={translations.title}
      submitDisabled={submitDisabled}
      cancelText={translations.cancel}
      submitText={translations.save}
      cancelOnEscape
      onClose={() => onResolve(null)}
      onCancel={() => onResolve(null)}
      onSubmit={handleSubmit}
    >
      <Wrapper>
        {loading ? (
          <div className="loader">
            <Loader.Dots />
          </div>
        ) : (
          <>
            {!!overlappingEntry && (
              <Overlapping>
                <p>{translations.overlapping}</p>

                <div className="entry">
                  <div className="icon">
                    <SVGIcon icon="clock" width={CLOCK_ICON_WIDTH} />
                  </div>

                  <div className="timespan">
                    <span>{datetimeStart}</span>
                    <span>{datetimeEnd}</span>
                  </div>

                  <div className="comment">
                    {overlappingEntry.comment || (
                      <span>{translations.noComment}</span>
                    )}
                  </div>

                  <div className="check">
                    <Checkbox
                      checked={extendOverlapping}
                      label={translations.extend}
                      boxWidth="1.5rem"
                      boxHeight="1.5rem"
                      fontSize="0.8rem"
                      onChange={setExtendOverlapping}
                    />
                  </div>
                </div>
              </Overlapping>
            )}

            <Label htmlFor="comment">{translations.commentLabel}</Label>
            <TextArea
              id="comment"
              value={comment}
              autoFocus
              fullWidth
              onChange={setComment}
            />
          </>
        )}
      </Wrapper>
    </RegularModal>
  )
}
